import { useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Camera, Loader2, Sparkles, ArrowRight, RefreshCw, CheckCircle2, Car } from "lucide-react";
import { toast } from "sonner";
import { Reveal } from "@/components/common/Reveal";
import { api } from "@/lib/api";

async function compressToBase64(file, max = 1200, quality = 0.72) {
  const dataUrl = await new Promise((res, rej) => {
    const r = new FileReader();
    r.onload = () => res(r.result);
    r.onerror = rej;
    r.readAsDataURL(file);
  });
  const img = await new Promise((res, rej) => {
    const i = new Image();
    i.onload = () => res(i);
    i.onerror = rej;
    i.src = dataUrl;
  });
  const scale = Math.min(1, max / Math.max(img.width, img.height));
  const canvas = document.createElement("canvas");
  canvas.width = Math.round(img.width * scale);
  canvas.height = Math.round(img.height * scale);
  canvas.getContext("2d").drawImage(img, 0, 0, canvas.width, canvas.height);
  return canvas.toDataURL("image/jpeg", quality);
}

const CONDITION_STYLE = {
  light: "bg-emerald-100 text-emerald-700",
  moderate: "bg-amber-100 text-amber-700",
  heavy: "bg-brand-red/10 text-brand-red",
};

export default function InstantQuote() {
  const fileRef = useRef(null);
  const [preview, setPreview] = useState(null);
  const [b64, setB64] = useState(null);
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  const onPick = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      const compressed = await compressToBase64(file);
      setB64(compressed);
      setPreview(compressed);
      setResult(null);
    } catch {
      toast.error("Couldn't read that image. Try a JPG or PNG photo.");
    }
  };

  const submit = async () => {
    if (!b64) return;
    setLoading(true);
    try {
      const { data } = await api.post("/quote/photo", {
        image_base64: b64,
        name: name || null,
        email: email || null,
      });
      setResult(data);
      if (data.emailed) toast.success("Estimate sent to your inbox too ✉️");
    } catch (err) {
      const msg = err?.response?.data?.detail || "Something went wrong — please try again.";
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  const reset = () => {
    setPreview(null); setB64(null); setResult(null);
    if (fileRef.current) fileRef.current.value = "";
  };

  return (
    <section data-testid="instant-quote" className="bg-white">
      <div className="container-rv py-24 lg:py-32">
        <div className="overflow-hidden rounded-3xl bg-brand-charcoal">
          <div className="grid lg:grid-cols-2">
            {/* Left: pitch */}
            <div className="p-10 lg:p-14">
              <p className="overline text-brand-red">Instant AI estimate</p>
              <h2 className="mt-4 font-serif text-4xl font-bold leading-tight text-white sm:text-5xl">
                Snap a photo.<br />Get a price in seconds.
              </h2>
              <p className="mt-5 max-w-md text-brand-silver">
                Not sure what your car needs? Take a quick photo and our AI sizes up your vehicle and how
                dirty it is, then gives you a ballpark price — no waiting, no phone tag.
              </p>
              <ul className="mt-7 space-y-3 text-sm text-white/85">
                {["Works with any car, truck, SUV or van", "Free & instant — no commitment", "Final price always confirmed on-site"].map((t) => (
                  <li key={t} className="flex items-center gap-2.5">
                    <CheckCircle2 className="h-4 w-4 shrink-0 text-brand-red" /> {t}
                  </li>
                ))}
              </ul>
            </div>

            {/* Right: uploader / result */}
            <div className="border-t border-white/10 bg-white/[0.03] p-8 lg:border-l lg:border-t-0 lg:p-12">
              <input
                ref={fileRef}
                type="file"
                accept="image/*"
                capture="environment"
                onChange={onPick}
                className="hidden"
                data-testid="quote-file-input"
              />

              <AnimatePresence mode="wait">
                {!result ? (
                  <motion.div key="upload" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
                    <button
                      onClick={() => fileRef.current?.click()}
                      data-testid="quote-upload-trigger"
                      className="group flex aspect-video w-full flex-col items-center justify-center gap-3 overflow-hidden rounded-2xl border-2 border-dashed border-white/25 text-white/70 transition-colors hover:border-brand-red hover:text-white"
                    >
                      {preview ? (
                        <img src={preview} alt="Your car" data-testid="quote-preview" className="h-full w-full object-cover" />
                      ) : (
                        <>
                          <Camera className="h-9 w-9 transition-transform group-hover:scale-110" />
                          <span className="text-sm font-medium">Tap to take or upload a photo</span>
                        </>
                      )}
                    </button>

                    {preview && (
                      <div className="mt-5 space-y-3">
                        <div className="grid grid-cols-2 gap-3">
                          <input
                            value={name} onChange={(e) => setName(e.target.value)}
                            placeholder="First name (optional)" data-testid="quote-name-input"
                            className="rounded-xl border border-white/15 bg-white/5 px-4 py-2.5 text-sm text-white placeholder:text-white/40 focus:border-brand-red focus:outline-none"
                          />
                          <input
                            value={email} onChange={(e) => setEmail(e.target.value)}
                            placeholder="Email (to save it)" type="email" data-testid="quote-email-input"
                            className="rounded-xl border border-white/15 bg-white/5 px-4 py-2.5 text-sm text-white placeholder:text-white/40 focus:border-brand-red focus:outline-none"
                          />
                        </div>
                        <div className="flex gap-3">
                          <button onClick={submit} disabled={loading} data-testid="quote-submit"
                            className="btn-red flex-1 disabled:opacity-60">
                            {loading ? <><Loader2 className="h-4 w-4 animate-spin" /> Analyzing…</> : <><Sparkles className="h-4 w-4" /> Get my estimate</>}
                          </button>
                          <button onClick={() => fileRef.current?.click()} className="btn-outline-light" data-testid="quote-retake">
                            <RefreshCw className="h-4 w-4" />
                          </button>
                        </div>
                      </div>
                    )}
                  </motion.div>
                ) : (
                  <motion.div key="result" initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}
                    data-testid="quote-result" className="text-white">
                    <div className="flex items-center gap-2 text-brand-silver">
                      <Car className="h-4 w-4 text-brand-red" />
                      <span className="text-sm">{result.vehicle_label} · {result.color}</span>
                      <span className={`ml-auto rounded-full px-2.5 py-0.5 text-[0.65rem] font-semibold uppercase tracking-wider ${CONDITION_STYLE[result.condition] || CONDITION_STYLE.moderate}`}>
                        {result.condition} soil
                      </span>
                    </div>

                    <p className="mt-5 text-sm text-brand-silver">Recommended: <b className="text-white">{result.service_name}</b></p>
                    <p className="mt-1 font-serif text-5xl font-bold text-white" data-testid="quote-price">
                      ${result.estimate_low}<span className="text-brand-silver"> – </span>${result.estimate_high}
                    </p>
                    {result.summer_note && (
                      <p className="mt-1 text-xs font-semibold text-brand-red">{result.summer_note}</p>
                    )}

                    {result.observation && (
                      <p className="mt-4 rounded-xl bg-white/5 p-4 text-sm text-white/80">“{result.observation}”</p>
                    )}
                    {result.emailed && (
                      <p className="mt-3 flex items-center gap-1.5 text-xs text-emerald-400"><CheckCircle2 className="h-3.5 w-3.5" /> A copy is on its way to your inbox.</p>
                    )}

                    <div className="mt-6 flex flex-wrap gap-3">
                      <Link to={`/book?service=${result.service}`} data-testid="quote-book-cta" className="btn-red flex-1">
                        Lock in your booking <ArrowRight className="h-4 w-4" />
                      </Link>
                      <button onClick={reset} className="btn-outline-light" data-testid="quote-try-another">
                        Try another photo
                      </button>
                    </div>
                    <p className="mt-4 text-[0.7rem] text-white/40">Rough AI estimate — final price confirmed on-site, no surprises.</p>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
